import React, { useRef, useState } from "react";
import { Canvas, useLoader } from "@react-three/fiber";
import { TextureLoader, Mesh, Vector3, Texture } from "three";
import grassTexture from "./grass.svg"; // Replace with the correct paths to your SVGs
import stoneTexture from "./stone.svg";
import sandTexture from "./sand.svg";
import waterTexture from "./water.svg";
import roadTexture from "./road.svg";
import CameraControls from "camera-controls";
import {
  CameraControls as CameraController,
  Environment,
} from "@react-three/drei";

import hexMap from "./hex_map.json";
//import { Pathtracer } from "@react-three/gpu-pathtracer";

import "./hexagons.css";

// Define the type for a hex tile
interface HexTile {
  q: number;
  r: number;
  height: number;
  texture: number;
  rotation: number;
}

// Convert hex axial coordinates to Cartesian coordinates
const hexToPixel = (
  q: number,
  r: number,
  a: number = 1
): { x: number; y: number } => {
  const x = a * (3 / 2) * q;
  const y = a * Math.sqrt(3) * (r + q / 2);
  return { x, y };
};

interface HexagonTubeProps {
  q: number;
  r: number;
  size: number;
  height: number;
  texture: number;
  rotation: number; // 1-6 for hexagon orientation
}

const textures = [
  grassTexture,
  stoneTexture,
  sandTexture,
  waterTexture,
  roadTexture,
];

const HexagonTube: React.FC<HexagonTubeProps> = ({
  q,
  r,
  size,
  height,
  texture,
  rotation,
}) => {
  const ref = useRef<Mesh>(null);
  const { x, y } = hexToPixel(q, r, size);

  const [textureEnum, setTextureEnum] = useState(texture);

  const [textureMap, setTextureMap] = useState<Texture>(
    useLoader(TextureLoader, textures[textureEnum])
  );

  const useClicked = () => {
    const rotationY = (2 * Math.PI) / 3;
    ref.current?.rotateOnAxis(new Vector3(0, 1, 0), rotationY);
  };

  const useRightClicked = () => {
    setTextureEnum((i) => (i + 1) % 5);
    setTextureMap(useLoader(TextureLoader, textures[textureEnum]));
  };

  return (
    <mesh
      ref={ref}
      key={"x:%x y:%y"}
      position={[x, height / 2, y]}
      rotation={[0, (2 * (rotation * Math.PI) + 3) / 6.0, 0]}
      onClick={useClicked}
      onContextMenu={useRightClicked}
    >
      <cylinderGeometry args={[size, size, height, 6]} />
      <meshStandardMaterial map={textureMap} />
    </mesh>
  );
};

interface HexagonGridProps {
  hexagons: HexTile[];
  size: number;
}

const HexagonGrid: React.FC<HexagonGridProps> = ({ hexagons, size }) => {
  return (
    <>
      {hexagons.map((tile: HexTile, index) => (
        <HexagonTube
          key={index}
          q={tile.q}
          r={tile.r}
          size={size}
          height={tile.height}
          rotation={tile.rotation}
          texture={tile.texture}
        />
      ))}
    </>
  );
};

const Hexagons: React.FC = () => {
  const size = 5; // Edge length of each hexagon

  return (
    <Canvas camera={{ position: [0, 25, 100], fov: 50 }}>
      <CameraController
        makeDefault
        colliderMeshes={[]}
        minZoom={5.0}
        maxZoom={100}
        polarRotateSpeed={1.0}
        azimuthRotateSpeed={1.0}
        dollySpeed={0.7}
        truckSpeed={2.0}
        maxDistance={200}
        minDistance={5.0}
        minPolarAngle={0}
        maxPolarAngle={(15 * Math.PI) / 32}
        touches={{
          one: CameraControls.ACTION.TOUCH_TRUCK,
          two: CameraControls.ACTION.TOUCH_DOLLY_ROTATE,
          three: CameraControls.ACTION.NONE,
        }}
        mouseButtons={{
          left: CameraControls.ACTION.TRUCK,
          right: CameraControls.ACTION.ROTATE,
          middle: CameraControls.ACTION.OFFSET,
          wheel: CameraControls.ACTION.DOLLY,
        }}
      />

      {/*<Suspense fallback={null}>
          <Pathtracer
            key={pathtracerKey}
            samples={1}
            bounces={4}
            resolutionFactor={1.0}
            tiles={[1, 1]}
            enabled={true}
            backgroundIntensity={3.0}
            backgroundBlur={0.4}
          >*/}
      <Environment preset={"dawn"} background={false} />
      <HexagonGrid hexagons={hexMap} size={size} />
      {/* </Pathtracer> 
        </Suspense> */}
    </Canvas>
  );
};

export default Hexagons;
